import React, { FC } from 'react';
import moment from 'moment';
import pluralize from 'pluralize';
import { getDurationNicknameFromMs } from '@fullstory/util/date';
import { activityNameFromPath, isLive } from '@fullstory/util/session';
import { EventMarker, SearchUser } from '@fullstory/proto/fs/services/alexa';
import { Typography } from '@fullstory/components';
import { Duration, Timestamp } from '@fullstory/proto/google/protobuf';
import { Platform } from '@fullstory/proto/fs/api/segment';

type SessionInfoDetailCopyProp = {
  completeSessions: boolean;
  eventMarkers: EventMarker[];
  now: Date;
  numSessions: number;
  sessionLinkHref: string;
  user: SearchUser;
};

export const SessionInfoDetailCopy: FC<SessionInfoDetailCopyProp> = ({
  completeSessions,
  eventMarkers,
  now: nowProp,
  numSessions,
  sessionLinkHref,
  user,
}) => {
  const now = moment(nowProp);
  const sessionStart = moment(Timestamp.toMillis(user.lastMatchingSessionStartAt));
  const lastEventTime = moment(Timestamp.toMillis(user.lastMatchingEventStartAt));
  const sessionLength = moment.duration(
    Duration.toSeconds(user.lastMatchingSessionDuration),
    'seconds',
  );
  const durationText = isLive(now, lastEventTime, sessionStart, sessionLength, completeSessions)
    ? 'Live'
    : getDurationNicknameFromMs(sessionLength.asMilliseconds()); // Last session duration / "Live"
  const eventsText = getEventsText(user, user.fetchClips, eventMarkers, numSessions);
  const lastPageText = getLastPageText(user);
  const serverEventName = !sessionLinkHref ? user.lastMatchingEventName : '';
  // Hide the duration / last page when the event text is set by event markers
  const durationToLastPageText =
    !eventMarkers || !eventMarkers.length ? (
      <>
        &middot; {durationText} &middot; {lastPageText}
      </>
    ) : null;
  return (
    <div data-component="SessionInfoDetailCopy" className="events fs-block" title={lastPageText}>
      <Typography color="neutrals-n7" variant="heading7" ellipsize tag="div">
        {getDetailCopy(serverEventName, eventsText, durationToLastPageText)}
      </Typography>
    </div>
  );
};

function getDetailCopy(
  serverEventName: string,
  eventText: string,
  durationToLastPageText: null | JSX.Element,
) {
  // if it is a serveronly event then we show the sever event name
  if (serverEventName) {
    return serverEventName;
  }

  return (
    <>
      {eventText}
      {durationToLastPageText ? durationToLastPageText : null}
    </>
  );
}

function getEventsText(
  user: SearchUser,
  isClip: boolean,
  eventMarkers: EventMarker[],
  numSessions: number,
): string {
  if (!isClip) {
    return `${user.lastMatchingSessionNumEvents} ${pluralize(
      'event',
      user.lastMatchingSessionNumEvents,
    )}`;
  }

  if (numSessions === 0) {
    // TODO(clay): show something better here, like server event name or other offline activity details
    return `Offline activity`;
  } else if (numSessions === 1) {
    return `${eventMarkers.length} matching ${pluralize(
      'event',
      eventMarkers.length,
    )} in this session`;
  }
  return `Spans ${numSessions} sessions`;
}

function getLastPageText(user: SearchUser): string {
  if (user.lastMatchingPlatform == Platform.PLATFORM_NATIVEMOBILE) {
    return activityNameFromPath(user.lastMatchingPage);
  }
  if (user.lastMatchingPage) {
    try {
      const url = new URL(user.lastMatchingPage);
      if (url.pathname == '/') {
        return url.hostname;
      }
      return url.pathname;
    } catch (e) {
      console.error('lastMatchingPage is invalid ', e);
    }
  }
  return '';
}
